import { useCallback, useEffect, useRef, useState } from 'react';

import GetEventShares from '@/connection/analytics/project-level/get-event-shares';
import GetEventUniqueAttendance from '@/connection/analytics/get-event-unique-attendance';
import GetEventAverageUserWatchtime from '@/connection/analytics/get-event-average-user-watchtime';
import { IAnalyticsScorecard } from '@/types/analytics';
import GetLiveAndOnDemandEventViews from '@/connection/analytics/project-level/get-live-and-on-demand-event-views';
import { useAppSelector } from '@/store/hooks';
import { stageEnvironment } from '@/utils/urls';
import { getPublishedStatus, selectProjectCreatedDate } from '@/store/slices/project/selectors';
import { EProjectStatus } from '@/types/projects.types';
import GetAverageProjectRating from '@/connection/analytics/project-level/get-average-project-rating';

// moved to use-fetch-project-analytics.ts with a different type response
// import GetEventTotalInvites from '@/connection/analytics/get-event-total-invites';
import sleep from '@/utils/sleep';
import GetEventRegistrations from '@/connection/analytics/get-event-registrations';

export enum EStatKeys {
  avgUserWatchtime = 'avgUserWatchtime',
  uniqueAttendees = 'uniqueAttendees',
  attendanceRate = 'attendanceRate',
  projectViews = 'projectViews',
  averageProjectRating = 'averageProjectRating',
  interactions = 'interactions',
  likes = 'likes',
  shares = 'shares',
  // invites = 'invites',
  registrations = 'registrations',
}

export type TStats = {
  [EStatKeys.avgUserWatchtime]: IAnalyticsScorecard | null;
  [EStatKeys.uniqueAttendees]: IAnalyticsScorecard | null;
  [EStatKeys.attendanceRate]: Pick<IAnalyticsScorecard, 'value' | 'loading'> | null;
  [EStatKeys.projectViews]: IAnalyticsScorecard | null;
  [EStatKeys.averageProjectRating]: IAnalyticsScorecard | null;
  [EStatKeys.interactions]: IAnalyticsScorecard | null;
  [EStatKeys.likes]: IAnalyticsScorecard | null;
  [EStatKeys.shares]: IAnalyticsScorecard | null;
  [EStatKeys.registrations]: IAnalyticsScorecard | null;
  // moved to use-fetch-project-analytics.ts with a different type response
  // [EStatKeys.invites]: IAnalyticsScorecard | null;
};

type ProjectStatsRowProps = {
  projectUuid?: string;
  language?: string;
  disabled?: boolean;
  stats?: Array<EStatKeys>;
};

const useFetchEventOverviewStats = ({
  projectUuid,
  language,
  disabled,
  stats = [
    EStatKeys.uniqueAttendees,
    EStatKeys.avgUserWatchtime,
    EStatKeys.registrations,
    EStatKeys.averageProjectRating,
    EStatKeys.interactions,
    EStatKeys.shares,
  ],
}: ProjectStatsRowProps) => {
  const fetching = useRef(false);

  const createdDate = useAppSelector(selectProjectCreatedDate);
  const publishedStatus = useAppSelector(getPublishedStatus);

  const isPublished = publishedStatus?.status === EProjectStatus.PUBLIC;

  const abortControllersRef = useRef({
    avgUserWatchtimesController: new AbortController(),
    uniqueAttendeesController: new AbortController(),
    attendanceRateController: new AbortController(),
    eventViewsController: new AbortController(),
    averageProjectRatingController: new AbortController(),
    interactionsController: new AbortController(),
    likesController: new AbortController(),
    sharesController: new AbortController(),
    registrationsController: new AbortController(),
    // moved to use-fetch-project-analytics.ts with a different type response
    // invitesController: new AbortController(),
  });

  const [data, setData] = useState<TStats>({
    avgUserWatchtime: null,
    uniqueAttendees: null,
    attendanceRate: null,
    projectViews: null,
    averageProjectRating: null,
    interactions: null,
    likes: null,
    shares: null,
    registrations: null,
    // moved to use-fetch-project-analytics.ts with a different type response
    // invites: null,
  });

  const abort = useCallback(() => {
    const controllers = abortControllersRef.current;
    fetching.current = false;
    Object.values(controllers).forEach((controller) => !controller.signal.aborted && controller.abort());
  }, []);

  const handleResponse = useCallback(
    (key: EStatKeys) => (res: IAnalyticsScorecard | null) => {
      setData((prev) => ({ ...prev, [key]: { ...res, loading: false } }));
    },
    []
  );

  const handleLoad = useCallback((key: EStatKeys) => {
    setData((prev) => ({ ...prev, [key]: { value: null, loading: true } }));
  }, []);

  const handleError = useCallback(
    (key: EStatKeys) => (err: Error) => {
      console.error(`Error fetching ${key}:`, err);
      setData((prev) => ({ ...prev, [key]: null }));
    },
    []
  );

  useEffect(() => {
    if (!isPublished) return;
    if (disabled) return;
    if (!projectUuid) return;
    if (fetching.current) return;

    (async () => {
      try {
        fetching.current = true;
        const controllers = abortControllersRef.current;

        const start = new Date(createdDate || 0).valueOf();

        if (stats.includes(EStatKeys.avgUserWatchtime)) {
          handleLoad(EStatKeys.avgUserWatchtime);
          GetEventAverageUserWatchtime({ projectUuid, start, signal: controllers.avgUserWatchtimesController.signal })
            .then(handleResponse(EStatKeys.avgUserWatchtime))
            .catch(handleError(EStatKeys.avgUserWatchtime));

          await sleep(300); // avoid sending all requests at once, which can cause server overload
        }

        if (stats.includes(EStatKeys.uniqueAttendees)) {
          handleLoad(EStatKeys.uniqueAttendees);
          GetEventUniqueAttendance({ projectUuid, start, signal: controllers.uniqueAttendeesController.signal })
            .then(handleResponse(EStatKeys.uniqueAttendees))
            .catch(handleError(EStatKeys.uniqueAttendees));
          await sleep(300); // avoid sending all requests at once, which can cause server overload
        }

        // Note: this is "interactions" which is really just "page views"
        if (stats.includes(EStatKeys.interactions)) {
          handleLoad(EStatKeys.interactions);
          GetLiveAndOnDemandEventViews({ projectUuid, start, signal: controllers.interactionsController.signal })
            .then(handleResponse(EStatKeys.interactions))
            .catch(handleError(EStatKeys.interactions));
          await sleep(300); // avoid sending all requests at once, which can cause server overload
        }

        if (stats.includes(EStatKeys.registrations)) {
          handleLoad(EStatKeys.registrations);
          GetEventRegistrations({
            projectUuid,
            start,
            end: Date.now(),
            signal: controllers.registrationsController.signal,
          })
            .then(handleResponse(EStatKeys.registrations))
            .catch(handleError(EStatKeys.registrations));
        }

        // INCORRECT API CALL
        // TODO: feature doesn't exist yet
        // GetEventLikes({ projectUuid, signal: controllers.attendanceRateController.signal })
        //   .then(handleResponse(EStatKeys.attendanceRate))
        //   .catch(handleError(EStatKeys.attendanceRate))
        //   .finally(handleFinally);

        // moved to use-fetch-project-analytics.ts with a different type response
        // if (stats.includes(EStatKeys.invites)) {
        //   handleLoad(EStatKeys.invites);
        //   GetEventTotalInvites({
        //     projectUuid,
        //     signal: controllers.invitesController.signal,
        //   })
        //     .then(handleResponse(EStatKeys.invites))
        //     .catch(handleError(EStatKeys.invites));
        //   await sleep(300); // avoid sending all requests at once, which can cause server overload
        // }

        if (stats.includes(EStatKeys.shares)) {
          handleLoad(EStatKeys.shares);
          GetEventShares({
            projectUuid,
            start,
            end: Date.now(),
            signal: controllers.sharesController.signal,
          })
            .then(handleResponse(EStatKeys.shares))
            .catch(handleError(EStatKeys.shares));
          await sleep(300); // avoid sending all requests at once, which can cause server overload
        }

        if (stats.includes(EStatKeys.averageProjectRating)) {
          handleLoad(EStatKeys.averageProjectRating);
          GetAverageProjectRating({ projectUuid, start, signal: controllers.averageProjectRatingController.signal })
            .then(handleResponse(EStatKeys.averageProjectRating))
            .catch(handleError(EStatKeys.averageProjectRating));
        }
      } catch (error) {
        console.error('Failed to fetch project stats:', error);
      }
    })();

    return () => {
      // Note: on localhost (development mode), this cleanup always triggers because React.StrictMode forces hooks to trigger twice.
      // So the data on localhost is always null, therefore I'm not calling it on localhost.
      // It does not do so in remote environments.
      if (!stageEnvironment.isLocal) {
        // abort();
      }
    };
  }, [
    language,
    projectUuid,
    createdDate,
    abort,
    disabled,
    isPublished,
    handleResponse,
    handleError,
    handleLoad,
    stats,
  ]);

  useEffect(() => {
    const attendees = data[EStatKeys.uniqueAttendees];
    const registrations = data[EStatKeys.registrations];

    if (registrations && attendees && !registrations.loading && !attendees.loading && !attendees.subValue) {
      const regNum = registrations.value || 0;
      const uniqueAttendees = attendees.value || 0;
      // constrain to 2 decimal places unless it's a whole number
      const percentage = regNum ? ((uniqueAttendees / regNum) * 100).toFixed(2).replace('.00', '') : '0';

      setData((prev) => ({
        ...prev,
        [EStatKeys.uniqueAttendees]: {
          ...attendees,
          subValue: `of ${regNum.toLocaleString()} registered • ${percentage}%`,
        },
      }));
    }
  }, [data]);

  return { data, abort };
};

export default useFetchEventOverviewStats;
